import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Plus, Search, PoundSterling, Users, Wrench, Bell, Clock, ArrowRight, FileText, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAgent } from '../../contexts/AgentContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useToast } from '../../hooks/use-toast';
import { COMPANY } from '../../constants/company';
import { buildEmail } from '../../components/agent/emailTemplateBuilder';

// Auto-send a payment receipt email the moment an admin/agent marks a policy as Paid.
// Fires-and-forgets — we don't want a slow email to block the UI status change.
const _authHeaders = () => {
  try {
    const a = JSON.parse(localStorage.getItem('dhs-agent') || 'null') || JSON.parse(localStorage.getItem('dhs-admin') || 'null');
    return a?.token ? { Authorization: `Bearer ${a.token}` } : {};
  } catch { return {}; }
};

const sendReceiptEmail = async (policy) => {
  if (!policy?.email) return { ok: false, reason: 'no customer email' };
  try {
    const tpl = buildEmail('receipt', policy);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emails/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ..._authHeaders() },
      body: JSON.stringify({ to: policy.email, subject: tpl.subject, html: tpl.html, text: tpl.text, tag: 'receipt' }),
    });
    if (res.status === 401) return { ok: false, reason: 'Session expired — please sign in again', authExpired: true };
    const j = await res.json().catch(() => ({}));
    return { ok: res.ok && j.status === 'sent', j };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
};
import RenewalsWidget from '../../components/agent/RenewalsWidget';
import RenewalQueueWidget from '../../components/agent/RenewalQueueWidget';
import PartialPaymentDialog from '../../components/agent/PartialPaymentDialog';

const STATUS_STYLES = {
  'Paid': 'bg-[#00b67a]/15 text-[#00b67a]',
  'Payment Due': 'bg-[#FFEDD6] text-[#1F0068]',
  'Awaiting Payment': 'bg-[#FFE0D6] text-[#1F0068]',
  'Overdue': 'bg-[#FF5C39]/15 text-[#FF5C39]',
  'Partially Paid': 'bg-[#E8DDFF] text-[#1F0068]',
  'Refunded': 'bg-[#FCE0EC] text-[#1F0068]',
  'Cancelled': 'bg-[#1F0068]/10 text-[#1F0068]/70',
};

const Stat = ({ label, value, icon: Icon, tone = 'bg-white text-[#1F0068]' }) => (
  <div className={`rounded-2xl p-5 border border-[#1F0068]/5 ${tone}`}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80"><Icon className="w-4 h-4" /> {label}</div>
    <div className="mt-1 text-3xl font-extrabold">{value}</div>
  </div>
);

const AgentDashboard = () => {
  const { agent, logout } = useAgent();
  const { policies, followUps, revenue, setPaymentStatus, recordPartialPayment, updatePolicy } = usePlatform();
  const nav = useNavigate();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partialFor, setPartialFor] = useState(null); // policy currently being asked "how much did they pay?"

  const rev = revenue();

  const filtered = policies.filter((p) => {
    if (statusFilter !== 'all' && p.paymentStatus !== statusFilter) return false;
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [p.planNumber, p.firstName, p.lastName, p.email, p.postcode].some((v) => (v || '').toLowerCase().includes(s));
  });

  const totalPending = policies.filter((p) => ['Payment Due', 'Awaiting Payment', 'Overdue', 'Partially Paid'].includes(p.paymentStatus)).length;
  const todayCust = policies.filter((p) => new Date(p.createdAt).toDateString() === new Date().toDateString()).length;

  const reminderEmail = (p) => `Subject: Payment reminder \u2013 Plan ${p.planNumber}\n\nDear ${p.firstName},\n\nWe hope you\u2019re well. This is a friendly reminder that payment for your Domestic Home Services Maintenance Cover Plan ${p.planNumber} for \u00a3${Number(p.amount).toFixed(2)} is still outstanding.\n\nTo avoid any interruption to your cover, please make payment via Direct Bank Transfer using the details below:\n\nAccount Name: ${COMPANY.bank.accountName}\nSort Code: ${COMPANY.bank.sortCode}\nAccount Number: ${COMPANY.bank.accountNumber}\nReference: ${p.planNumber}\n\nIf you have already sent payment, please ignore this reminder \u2014 it can take 1\u20132 working days to reach our account.\n\nAny questions? Just reply to this email or call ${COMPANY.phoneShort}.\n\nKind regards,\nDomestic Home Services Customer Care Team\n${COMPANY.email}`;

  const copyReminder = async (p) => {
    try { await navigator.clipboard.writeText(reminderEmail(p)); toast({ title: 'Reminder email copied', description: 'Paste into Outlook or Gmail to send.' }); } catch { toast({ title: 'Could not copy \u2014 select and copy manually' }); }
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA]">
      <header className="bg-[#0F0532] text-white">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/agent" className="flex items-center gap-2">
            <svg viewBox="0 0 40 40" className="w-8 h-8"><circle cx="20" cy="20" r="20" fill="#FF5C39" /><path d="M12 22 L18 28 L28 14" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <div className="leading-tight"><div className="font-bold text-sm">Domestic Home Services</div><div className="text-[10px] tracking-[0.22em] text-white/60 uppercase">Agent Portal</div></div>
          </Link>
          <div className="flex items-center gap-3 text-sm"><div className="hidden md:block text-white/75">Signed in as <b className="text-white">{agent?.name}</b></div><Button variant="ghost" onClick={() => { logout(); nav('/agent/login'); }} className="gap-1.5 text-white/85 hover:bg-white/10 rounded-full"><LogOut className="w-4 h-4" /> Sign out</Button></div>
        </div>
      </header>

      <main className="py-8">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div><h1 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight">Dashboard</h1><p className="text-[#1F0068]/70">Revenue is counted only after a customer is marked <b>Paid</b>.</p></div>
            <Link to="/agent/new"><Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white h-11 px-6 gap-1.5 font-semibold"><Plus className="w-4 h-4" /> New policy</Button></Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            <Stat label="Today’s Revenue" value={`£${rev.today.toFixed(2)}`} icon={PoundSterling} tone="bg-[#1F0068] text-white" />
            <Stat label="Weekly Revenue" value={`£${rev.week.toFixed(2)}`} icon={PoundSterling} />
            <Stat label="Monthly Revenue" value={`£${rev.month.toFixed(2)}`} icon={PoundSterling} tone="bg-[#FF5C39] text-white" />
            <Stat label="Yearly Revenue" value={`£${rev.year.toFixed(2)}`} icon={PoundSterling} />
            <Stat label="Today’s Customers" value={todayCust} icon={Users} />
            <Stat label="Pending Payments" value={totalPending} icon={Bell} />
            <Stat label="Follow-ups (>24h)" value={followUps.length} icon={Clock} />
            <Stat label="Pending Services" value={0} icon={Wrench} />
          </div>

          <div className="mb-6">
            <RenewalsWidget policies={policies} />
          </div>

          <div className="mb-6">
            <RenewalQueueWidget policies={policies} />
          </div>

          {followUps.length > 0 && (
            <div className="mb-6 bg-[#FF5C39]/10 border border-[#FF5C39]/30 rounded-2xl p-4 md:p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div><div className="font-bold text-[#FF5C39]"><Bell className="w-4 h-4 inline mr-1" /> {followUps.length} payment follow-up{followUps.length > 1 ? 's' : ''}</div><div className="text-xs text-[#1F0068]/70">Paperwork issued over 24 hours ago and still unpaid.</div></div>
              </div>
              <div className="mt-3 grid md:grid-cols-2 gap-2">
                {followUps.slice(0, 4).map((p) => (
                  <div key={p.id} className="bg-white rounded-xl p-3 flex items-center justify-between text-sm">
                    <div><div className="font-semibold text-[#1F0068]">{p.firstName} {p.lastName}</div><div className="text-xs text-[#1F0068]/60">{p.planNumber} · £{Number(p.amount).toFixed(2)}</div></div>
                    <div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => copyReminder(p)} className="rounded-full text-xs"><Copy className="w-3 h-3 mr-1" /> Copy email</Button><Link to={`/agent/policy/${p.id}`}><Button size="sm" className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white text-xs">Open</Button></Link></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#1F0068]/5 overflow-hidden">
            <div className="p-4 border-b border-[#1F0068]/5 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]"><Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, plan number, postcode…" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-11 rounded-xl bg-[#FFF6EB] border border-[#1F0068]/10 px-3 text-sm font-medium text-[#1F0068]">
                <option value="all">All statuses</option>
                {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF6EB] text-[#1F0068]/70 text-xs uppercase tracking-wider"><tr><th className="text-left px-5 py-3">Plan #</th><th className="text-left px-5 py-3">Customer</th><th className="text-left px-5 py-3">Phone</th><th className="text-left px-5 py-3">Amount</th><th className="text-left px-5 py-3">Payment</th><th className="text-right px-5 py-3">Actions</th></tr></thead>
                <tbody className="divide-y divide-[#1F0068]/5">
                  {filtered.map((p) => (
                    <tr key={p.id} className="hover:bg-[#FFF6EB]">
                      <td className="px-5 py-3 font-bold text-[#1F0068]">{p.planNumber}</td>
                      <td className="px-5 py-3"><div className="font-semibold text-[#1F0068]">{p.title} {p.firstName} {p.lastName}</div><div className="text-xs text-[#1F0068]/60">{p.email}</div></td>
                      <td className="px-5 py-3 text-[#1F0068]/80">{p.mobile || p.phone || '—'}</td>
                      <td className="px-5 py-3 font-semibold text-[#1F0068]">£ {Number(p.amount || 0).toFixed(2)}<div className="text-[10px] text-[#1F0068]/60 uppercase font-bold">Annual</div></td>
                      <td className="px-5 py-3">
                        <select value={p.paymentStatus || 'Payment Due'}
                          disabled={p.status === 'Cancelled'}
                          onChange={async (e) => {
                            const next = e.target.value;
                            const prev = p.paymentStatus;
                            if (next === 'Partially Paid') {
                              // Ask the agent HOW MUCH the customer paid before recording it.
                              // The dialog handles the actual state mutation via recordPartialPayment.
                              e.target.blur();
                              setPartialFor(p);
                              return;
                            }
                            setPaymentStatus(p.id, next);
                            // Only auto-send receipt on the transition FROM non-paid TO paid,
                            // AND only if we haven't already sent one for this policy.
                            const alreadySent = !!p.receiptSentAt;
                            if (next === 'Paid' && prev !== 'Paid' && !alreadySent) {
                              toast({ title: `Status: Paid`, description: `Sending payment receipt to ${p.email}…` });
                              const r = await sendReceiptEmail(p);
                              if (r.ok) {
                                // Stamp the policy so we never auto-fire a second receipt
                                updatePolicy(p.id, { receiptSentAt: new Date().toISOString() });
                                toast({ title: 'Receipt emailed', description: `Payment receipt delivered to ${p.email}.` });
                              } else if (r.authExpired) {
                                toast({ title: 'Session expired', description: 'Status saved. Please sign in again to send the receipt.' });
                                setTimeout(() => { window.location.href = '/agent/login'; }, 1500);
                              } else {
                                toast({ title: 'Status saved — receipt email not sent', description: r.reason || r.j?.detail || 'Please send the receipt manually from the Emails panel.' });
                              }
                            } else if (next === 'Paid' && alreadySent) {
                              toast({ title: `Status: Paid`, description: 'Receipt already sent for this policy — no duplicate sent. Send it again from the Emails panel if needed.' });
                            } else {
                              toast({ title: `Status: ${next}` });
                            }
                          }}
                          className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border-0 ${STATUS_STYLES[p.paymentStatus] || 'bg-[#FFEDD6] text-[#1F0068]'} ${p.status === 'Cancelled' ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title={p.status === 'Cancelled' ? 'Policy is cancelled — use the "Mark cancellation paid" button on the policy detail.' : ''}
                        >
                          {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/agent/policy/${p.id}`} className="inline-flex items-center gap-1 text-[#FF5C39] font-bold hover:underline">Open <ArrowRight className="w-3.5 h-3.5" /></Link>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-[#1F0068]/60">No policies found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <PartialPaymentDialog
        open={!!partialFor}
        policy={partialFor}
        onClose={() => setPartialFor(null)}
        onConfirm={async ({ amount, note }) => {
          const p = partialFor;
          setPartialFor(null);
          if (!p) return;
          const res = recordPartialPayment(p.id, amount, note);
          if (res.status === 'invalid') {
            toast({ title: 'Invalid amount' });
            return;
          }
          if (res.autoPaid) {
            // The last partial payment settled the balance. Auto-fire the receipt email
            // just like the Paid dropdown option does — unless one already went out.
            toast({ title: 'Balance fully paid', description: `Sending receipt to ${p.email}…` });
            if (!p.receiptSentAt) {
              const r = await sendReceiptEmail({ ...p, paymentStatus: 'Paid' });
              if (r.ok) {
                updatePolicy(p.id, { receiptSentAt: new Date().toISOString() });
                toast({ title: 'Receipt emailed', description: `Payment receipt delivered to ${p.email}.` });
              } else if (r.authExpired) {
                toast({ title: 'Session expired', description: 'Partial payment recorded. Please sign in again to send the receipt.' });
                setTimeout(() => { window.location.href = '/agent/login'; }, 1500);
              } else {
                toast({ title: 'Marked Paid — receipt email not sent', description: r.reason || r.j?.detail || 'Please send the receipt manually.' });
              }
            }
          } else {
            toast({ title: `£${Number(amount).toFixed(2)} recorded`, description: `£${res.outstanding.toFixed(2)} still outstanding on Plan ${p.planNumber}.` });
          }
        }}
      />
    </div>
  );
};

export default AgentDashboard;
